import {useCallback, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {Add as AddIcon, Straighten as MeasurementIcon} from "@mui/icons-material";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    rectSortingStrategy,
    arrayMove,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
    getTraineeMeasurementGrid,
    reorderTraineeMeasurements,
    hideTraineeMeasurement,
    unhideTraineeMeasurement,
    deletePersonalMeasurement,
    type TraineeMeasurementItem,
} from "../../../services/TraineeMeasurementService";
// @ts-ignore — untyped JS helper (TODO(ts-migration): convert CacheRtl.js)
import {RTL} from "../../../cache/CacheRtl";
import SortableMeasurementRow from "./SortableMeasurementRow";
import PersonalMeasurementDialog from "./PersonalMeasurementDialog";
import DeletePersonalMeasurementDialog from "./DeletePersonalMeasurementDialog";
import {useMessageContext} from "../../../contexts/MessageContext";
// @ts-ignore — untyped JS design tokens (TODO(ts-migration): convert coachTokens.js)
import {t} from "../../dashboard/coach/coachTokens";

interface Props {
    /**
     * The trainee whose measurement list is being configured.
     *
     * Passed the route's `userId`, matching every other admin measurement screen
     * (BodyMeasurementsTablePage, ClientBodyMeasurements) — they all feed the route id straight
     * into the server's `traineeId` path param on /admin/measurement/**.
     */
    traineeId: number;
}

/**
 * A trainer's per-trainee measurement configuration.
 *
 * Distinct from the company catalog screen (MeasurementDefinitionsScreen): nothing here affects
 * any other trainee. The trainer can hide a company measurement for this person, add a personal
 * measurement only they have, reorder the list, and permanently delete a personal one.
 *
 * The list deliberately INCLUDES hidden measurements — greyed out but still draggable, offering
 * only "un-hide". If they vanished there would be no way to bring one back, and their position
 * would be lost.
 */
const ClientMeasurementSettings = ({traineeId}: Props) => {
    const notify = useMessageContext()?.notificationHandler;

    const [rows, setRows] = useState<TraineeMeasurementItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingOrder, setSavingOrder] = useState(false);
    const [editing, setEditing] = useState<TraineeMeasurementItem | null>(null);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState<TraineeMeasurementItem | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    // Hidden measurements are pulled out of the grid into their own section at the bottom: they're
    // the exception, and interleaving greyed-out cards among the active ones made the grid hard to
    // read. They keep their slot in `rows` (and so in the saved order) — see handleDragEnd.
    const visible = rows.filter((r) => r.hidden !== true);
    const hiddenRows = rows.filter((r) => r.hidden === true);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await getTraineeMeasurementGrid(traineeId);
        setLoading(false);
        if (res.success) {
            setRows(res.data || []);
        } else {
            notify?.error(res.message || "אירעה שגיאה");
        }
        // notificationHandler is stable from context; excluding it keeps this from re-running.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [traineeId]);

    useEffect(() => {
        load();
    }, [load]);

    /**
     * Only VISIBLE cards are draggable — hidden ones live in their own section at the bottom and
     * are not part of the sortable set. Dragging therefore reorders the visible subset, and the
     * result is spliced back over the visible slots of the full list so the hidden rows keep the
     * positions they'd return to when un-hidden.
     *
     * The payload still carries the WHOLE list including hidden rows — dropping them would orphan
     * their order and send them to the end when un-hidden.
     */
    const handleDragEnd = async (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;

        const from = visible.findIndex((r) => r.id === active.id);
        const to = visible.findIndex((r) => r.id === over.id);
        if (from === -1 || to === -1) return;

        const movedVisible = arrayMove(visible, from, to);
        // Walk the full list, filling each visible slot from the reordered visible sequence and
        // leaving hidden rows exactly where they were.
        let next = 0;
        const reordered = rows.map((r) => (r.hidden === true ? r : movedVisible[next++]));
        setRows(reordered); // optimistic

        setSavingOrder(true);
        const items = reordered.map((r, i) => ({definitionId: r.id as number, displayOrder: i}));
        const res = await reorderTraineeMeasurements(traineeId, items);
        setSavingOrder(false);
        if (!res.success) {
            notify?.error(res.message || "אירעה שגיאה");
            load(); // revert to server truth
        }
    };

    const runAction = async (
        action: () => Promise<{success: boolean; message?: string}>,
    ) => {
        const res = await action();
        if (res.success) {
            if (res.message) notify?.success(res.message);
            load();
        } else {
            notify?.error(res.message || "אירעה שגיאה");
        }
    };

    /**
     * Why hiding this measurement for this trainee will be refused, or null.
     *
     * The server answers this per row (`formulaLock`) rather than the client deriving it: whether a
     * computed measurement locks its inputs depends on whether that formula is itself visible to
     * THIS trainee, which this list doesn't otherwise carry.
     */
    const lockReason = (item: TraineeMeasurementItem): string | null =>
        item.formulaLock ? `לא ניתן להסתיר — מדידה זו משמשת את "${item.formulaLock}"` : null;

    const handleHide = (item: TraineeMeasurementItem) =>
        runAction(() => hideTraineeMeasurement(traineeId, item.id as number));

    const handleUnhide = (item: TraineeMeasurementItem) =>
        runAction(() => unhideTraineeMeasurement(traineeId, item.id as number));

    const handleConfirmDelete = async () => {
        if (!deleting) return;
        const target = deleting;
        setDeleting(null);
        await runAction(() => deletePersonalMeasurement(traineeId, target.id as number));
    };

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "14px"}}>
            {/* Header */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px"}}>
                <Box>
                    <Typography sx={{fontSize: "15px", fontWeight: 700, color: t.text}}>
                        מדדי גוף של המתאמן
                    </Typography>
                    <Typography sx={{fontSize: "12px", color: t.t3, mt: "2px"}}>
                        גרור לשינוי הסדר · הסתר מדדים שאינם רלוונטיים · הוסף מדד אישי
                    </Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                    {savingOrder && (
                        <Typography sx={{fontSize: "12px", color: t.t3}}>שומר סדר…</Typography>
                    )}
                    <RTL>
                        <Button
                            startIcon={<AddIcon/>}
                            onClick={() => setCreating(true)}
                            sx={{
                                height: 34, px: "14px", borderRadius: t.rs,
                                background: t.green, color: "#fff", fontSize: "13px", fontWeight: 700,
                                "&:hover": {background: t.greenDark},
                            }}
                        >
                            מדד אישי
                        </Button>
                    </RTL>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: "40px"}}>
                    <CircularProgress size={26} sx={{color: t.green}}/>
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
                    py: "40px", borderRadius: t.rs, background: t.bg, border: `1px dashed ${t.border}`,
                }}>
                    <MeasurementIcon sx={{fontSize: 28, color: t.t3}}/>
                    <Typography sx={{fontSize: "13px", color: t.t2}}>
                        אין מדדים למתאמן זה
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Active measurements — a drag-and-drop grid, matching the company-level
                        per-gender grid so arranging measurements works the same in both places. */}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={visible.map((r) => r.id as number)}
                            strategy={rectSortingStrategy}
                        >
                            <Box data-testid="measurement-grid" sx={{
                                display: "grid", gap: "10px", direction: "rtl",
                                gridTemplateColumns: {xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)"},
                            }}>
                                {visible.map((item, index) => (
                                    <SortableMeasurementRow
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        lockReason={lockReason(item)}
                                        onEdit={() => setEditing(item)}
                                        onHide={() => handleHide(item)}
                                        onUnhide={() => handleUnhide(item)}
                                        onDelete={() => setDeleting(item)}
                                    />
                                ))}
                            </Box>
                        </SortableContext>
                    </DndContext>

                    {/* Hidden measurements — parked at the bottom, out of the way but recoverable.
                        Their order is preserved in `rows` and only matters once they're restored,
                        so there's nothing useful to arrange down here. They still render through
                        the sortable card (same look, and un-hide lives on it), so they get their
                        own DndContext: useSortable outside one warns, and a drag that reached the
                        page-level context would reorder the wrong list. onDragEnd is a no-op. */}
                    {hiddenRows.length > 0 && (
                        <Box data-testid="hidden-section" sx={{mt: "10px"}}>
                            <Typography sx={{fontSize: "12px", fontWeight: 700, color: t.t3, mb: "8px"}}>
                                מדדים מוסתרים ({hiddenRows.length})
                            </Typography>
                            <DndContext collisionDetection={closestCenter} onDragEnd={() => {}}>
                                <SortableContext
                                    items={hiddenRows.map((r) => r.id as number)}
                                    strategy={rectSortingStrategy}
                                >
                                    <Box sx={{
                                        display: "grid", gap: "10px", direction: "rtl",
                                        gridTemplateColumns: {xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)"},
                                    }}>
                                        {hiddenRows.map((item, index) => (
                                            <SortableMeasurementRow
                                                key={item.id}
                                                item={item}
                                                index={index}
                                                onEdit={() => setEditing(item)}
                                                onHide={() => handleHide(item)}
                                                onUnhide={() => handleUnhide(item)}
                                                onDelete={() => setDeleting(item)}
                                            />
                                        ))}
                                    </Box>
                                </SortableContext>
                            </DndContext>
                        </Box>
                    )}
                </>
            )}

            {(creating || editing) && (
                <PersonalMeasurementDialog
                    open
                    traineeId={traineeId}
                    item={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                />
            )}

            <DeletePersonalMeasurementDialog
                open={Boolean(deleting)}
                name={deleting ? (deleting.nameHeb || deleting.name || "") : ""}
                onCancel={() => setDeleting(null)}
                onConfirm={handleConfirmDelete}
            />
        </Box>
    );
};

export default ClientMeasurementSettings;
